import type {
  AudienceDefinition,
  AudienceDelivery,
  AudienceDestination,
  AudienceDisplayStatus,
  AudienceSnapshot
} from '../types';

export type DerivedAudienceStatus = {
  status: AudienceDisplayStatus;
  /** The snapshot's or failing delivery's error, set only when `status` is Failed. */
  errorMessage: string | null;
};

/** The status shown when a lookup it depends on failed. */
export const UNKNOWN_STATUS: DerivedAudienceStatus = { status: 'Unknown', errorMessage: null };

/**
 * Folds a definition, its latest snapshot and its destinations into one display status.
 *
 * Rules, first match wins:
 * 1. Deleted: the definition is deleted.
 * 2. Paused: the definition is paused.
 * 3. Failed: the snapshot failed, or a live destination's latest delivery failed.
 * 4. Building: the snapshot is queued or in progress, or there is no snapshot yet but there are live destinations.
 * 5. Delivering: the snapshot succeeded and a live destination is still pending, or a live destination's latest delivery is queued or in progress.
 * 6. Populating: a live destination is populating, or a live destination's latest delivery is populating.
 * 7. No destinations: there are no live destinations.
 * 8. Live: every live destination is active.
 *
 * `deliveries` are the latest snapshot's delivery attempts.
 * The argument is optional because the delivery list is internal-only.
 */
export const deriveAudienceStatus = (
  definition: Pick<AudienceDefinition, 'status'>,
  latestSnapshot: AudienceSnapshot | null,
  destinations: AudienceDestination[],
  deliveries: AudienceDelivery[] = []
): DerivedAudienceStatus => {
  const ok = (status: AudienceDisplayStatus): DerivedAudienceStatus => ({
    status,
    errorMessage: null
  });

  if (definition.status === 'deleted') return ok('Deleted');
  if (definition.status === 'paused') return ok('Paused');

  const live = destinations.filter(isLiveDestination);

  if (latestSnapshot?.status === 'failure')
    return { status: 'Failed', errorMessage: latestSnapshot.errorMessage };

  const latestDeliveries = latestDeliveryByDestination(deliveries);
  const outcomes = live.map(destination => latestDeliveries.get(destination.id));
  const failedDelivery = outcomes.find(delivery => delivery?.status === 'failure');
  if (failedDelivery) return { status: 'Failed', errorMessage: failedDelivery.errorMessage };

  if (latestSnapshot?.status === 'queued' || latestSnapshot?.status === 'inProgress')
    return ok('Building');
  if (!latestSnapshot && live.length > 0) return ok('Building');

  if (
    live.some(destination => destination.status === 'pending') ||
    outcomes.some(delivery => delivery != null && isDeliveryInFlight(delivery))
  )
    return ok('Delivering');
  if (
    live.some(destination => destination.status === 'populating') ||
    outcomes.some(delivery => delivery?.status === 'populating')
  )
    return ok('Populating');
  if (live.length === 0) return ok('No destinations');
  return ok('Live');
};

export const isLiveDestination = (destination: AudienceDestination): boolean =>
  destination.deletedAt == null;

/** The most recent attempt per destination, by id; assumes ids are minted in creation order. */
export const latestDeliveryByDestination = (
  deliveries: AudienceDelivery[]
): Map<number, AudienceDelivery> => {
  const latest = new Map<number, AudienceDelivery>();
  deliveries.forEach(delivery => {
    const current = latest.get(delivery.audienceDestinationId);
    if (!current || delivery.id > current.id) latest.set(delivery.audienceDestinationId, delivery);
  });
  return latest;
};

/** A delivery attempt that is queued or in progress, before it starts populating. */
export const isDeliveryInFlight = (delivery: AudienceDelivery): boolean =>
  delivery.status === 'queued' || delivery.status === 'inProgress';

/** A delivery attempt that will change without user action: queued, in progress, or populating. */
export const isDeliveryUnfinished = (delivery: AudienceDelivery): boolean =>
  isDeliveryInFlight(delivery) || delivery.status === 'populating';

export type DestinationDeliveryOutcome =
  | { kind: 'failed'; errorMessage: string | null }
  | { kind: 'delivering' }
  | { kind: 'populating' };

/**
 * What a live destination's latest delivery adds to its own status, or null when it adds nothing.
 *
 * - failed: the latest delivery failed, whatever the destination's status.
 * - delivering: an active destination's latest delivery is queued or in progress, as in a refresh.
 * - populating: an active destination's latest delivery is populating.
 *
 * A pending or populating destination already shows its progress, and a deleted one shows Deleted.
 */
export const destinationDeliveryOutcome = (
  destination: AudienceDestination,
  latestDelivery: AudienceDelivery | undefined
): DestinationDeliveryOutcome | null => {
  if (!latestDelivery || !isLiveDestination(destination)) return null;
  if (latestDelivery.status === 'failure')
    return { kind: 'failed', errorMessage: latestDelivery.errorMessage };
  if (destination.status !== 'active') return null;
  if (isDeliveryInFlight(latestDelivery)) return { kind: 'delivering' };
  if (latestDelivery.status === 'populating') return { kind: 'populating' };
  return null;
};

const IN_PROGRESS: readonly AudienceDisplayStatus[] = ['Building', 'Delivering', 'Populating'];

/* Rows in these states change without user action, so the viewer keeps polling */
export const isInProgressStatus = (status: AudienceDisplayStatus): boolean =>
  IN_PROGRESS.includes(status);

type PillVariant = 'default' | 'info' | 'success' | 'warning' | 'danger';

export const audienceStatusPillVariant = (status: AudienceDisplayStatus): PillVariant => {
  switch (status) {
    case 'Failed':
      return 'danger';
    case 'Building':
    case 'Delivering':
      return 'info';
    case 'Populating':
      return 'warning';
    case 'Live':
      return 'success';
    default:
      return 'default';
  }
};
